import { useTranslation } from 'react-i18next';
import { AiDocumentationFieldEnum, Documentation } from 'graphql-common';
import Chip, { ChipTypes } from '@lib/components/Chip/Chip';
import CollapseCell from '@lib/components/Table/commonComponents/CollapseCell/CollapseCell';
import { AiCredits } from 'components/AiAssistant/AiCreditsIndicator';
import { AiFieldUiState } from 'routes/Documentation/utils/getAiFieldDetectionState';
import AiFieldAssist from 'routes/Documentation/components/AiDetection/AiFieldAssist';
import useDocumentFieldDetection from 'routes/Documentation/DocumentView/utils/useDocumentFieldDetection';

type Props = {
  item: Documentation;
  isAiDetectionEnabled?: boolean;
  credits?: AiCredits | null;
};

export default function DocumentTagsCell({
  item,
  isAiDetectionEnabled,
  credits,
}: Props) {
  const { t } = useTranslation();
  const { tags: tagsDetection } = useDocumentFieldDetection({
    doc: item,
    documentationVersionId: item.currentVersion?.id,
    isAiEnabled: !!isAiDetectionEnabled,
  });

  if (!isAiDetectionEnabled || tagsDetection.uiState === AiFieldUiState.None) {
    return (
      <CollapseCell
        item={item}
        minHeight={24}
        value={(item.tags || []).map(({ id, name }) => (
          <Chip key={id} type={ChipTypes.chipBlack} label={name} />
        ))}
      />
    );
  }

  // CollapseCell's Props are `CellProps<T> & { minHeight; cropColor? }`
  // (`lib/src/components/Table/types.ts`): it destructures `value` directly
  // off props, exactly as passed above — matching the shape the table's own
  // `accessorFn` used to compute for the old `tags` column, so the plain
  // branch renders identically to today.

  return (
    <AiFieldAssist
      field={AiDocumentationFieldEnum.Tags}
      resolveSource={tagsDetection.resolveSource}
      onApplyTagNames={tagsDetection.onApplyTagNames}
      detectionUiState={tagsDetection.uiState}
      detectingLabel={t('ai-generating-tags')}
      onDismiss={tagsDetection.onDismiss}
      creditsRemaining={credits}
      labels={{
        idleLabel: t('ai-suggest-with-ai'),
        idleCta: t('ai-suggest-tags-cta'),
        regenerateCta: t('ai-regenerate'),
      }}
    />
  );
}
